import { SubscriptionPlan } from 'app/models/subscrition-plan.model';
import { GiftCardDuration } from '../models/enum/voucher-durations.enum';
import {
  FamilyOption,
  KinoSubscription,
  SubscriptionOrigins,
} from '../models/kino-subscription.model';
import { PaymentMethod } from '../models/payment-method.model';
import { Payment } from '../models/payment.interface';
import { FamilyOptionPlan, Plan, PlanTypes } from '../models/plan.model';
import { PrepaidCard } from '../models/prepaid-card.interface';
import { Voucher } from '../models/voucher.model';

export const convertApiDataToPlansAndFamilyOptionPlan = (
  apiData: any[]
): { plans: Plan[]; familyOptionPlan: FamilyOptionPlan | null } => {
  let familyOptionPlan: FamilyOptionPlan | null = null;
  const plans: Plan[] = [];
  for (const dataPlan of apiData) {
    const price: number = dataPlan.price;
    switch (dataPlan.name) {
      case 'monthly':
        plans.push({
          id: dataPlan.id,
          type: PlanTypes.Month,
          price,
          imageName: 'monthly-sub.png',
          canActivateFamilyOption: true,
        });
        break;
      case 'yearly':
        plans.push({
          id: dataPlan.id,
          type: PlanTypes.Year,
          price,
          imageName: 'yearly-sub.png',
          canActivateFamilyOption: true,
        });
        break;
      case 'lifetime':
        plans.push({
          id: dataPlan.id,
          type: PlanTypes.Life,
          price,
          imageName: 'lifetime-sub.png',
          canActivateFamilyOption: false,
        });
        break;
      case 'family':
        familyOptionPlan = {
          id: dataPlan.id,
          price,
        };
        break;
      default:
        break;
    }
  }
  return { plans, familyOptionPlan };
};

export const convertApiDataToSubscription = (
  apiData: any,
  plans: SubscriptionPlan[]
): KinoSubscription => {
  let origin: SubscriptionOrigins | undefined;
  if (apiData.type === 'inApp') {
    if ((apiData.app as string).toLowerCase().includes('android')) {
      origin = SubscriptionOrigins.Android;
    } else {
      origin = SubscriptionOrigins.Ios;
    }
  } else {
    origin = SubscriptionOrigins.Web;
  }

  let plan: SubscriptionPlan | undefined;
  if (apiData.type === 'voucher' || apiData.type === 'admin') {
    plan = {
      id: '',
      name: PlanTypes.PrepaidCard,
      price: 0,
      currency: 'eur',
      type: 'one_time',
      // canActivateFamilyOption: false,
    };
  } else {
    let planType: PlanTypes | undefined;
    switch (apiData.duration) {
      case 1:
        planType = PlanTypes.Month;
        break;
      case 12:
        planType = PlanTypes.Year;
        break;
      default:
        planType = PlanTypes.Life;
    }
    plan = { ...plans.find((plan) => plan.name === planType) };
  }

  const externalAdminEmail: string | null =
    apiData.type === 'family' ? apiData.main_email : null;

  return {
    id: apiData.id,
    plan,
    startDate: generateDateFromApiDataString(apiData.start_date),
    endDate:
      plan.name === PlanTypes.Life
        ? null
        : generateDateFromApiDataString(apiData.end_date),
    isAutoRenewed: apiData.auto_renew,
    familyOption: convertFamilyOption({
      apiFamilyOption: apiData.family_option,
      externalAdminEmail,
    }),
    origin,
  };
};

export const convertApiDataToSubscriptions = (
  apiData: any[],
  plans: SubscriptionPlan[]
): ReadonlyArray<KinoSubscription> => {
  return apiData.map((data) => {
    return { ...convertApiDataToSubscription(data, plans), renewalDate: null };
  });
};

export const convertApiDataToVouchers = (apiData: any): Voucher[] => {
  return apiData.map((item) => {
    let duration: GiftCardDuration;
    switch (item.duration) {
      case 6:
        duration = GiftCardDuration.SixMonths;
        break;
      case 12:
        duration = GiftCardDuration.OneYear;
        break;
      default:
        duration = GiftCardDuration.ForLife;
    }
    return {
      id: item.id,
      duration,
      purchaseDate: generateDateFromApiDataString(item.buy_date),
      useDate: generateDateFromApiDataString(item.use_date),
    };
  });
};

export const convertApiMetaDataToPayments = (apiMetaData: {
  data: any[];
}): Payment[] => {
  return apiMetaData.data.map((item: any) => {
    const date: Date = generateDateFromApiDataString(item.date);
    const endDate: Date = new Date(date);
    endDate.setMonth(date.getMonth() + item.duration);
    return {
      id: item.id,
      date: date,
      duration: item.duration,
      amount: item.price,
      currency: item.currency,
      isVoucher: item.type === 'voucher',
    };
  });
};

export const convertApiMetadataToPrepaidCard = (apiMetaData: {
  data: any[];
}): PrepaidCard[] => {
  if (!apiMetaData.data) {
    return [];
  }
  return apiMetaData.data?.map((item: any) => {
    const date: Date = new Date(item.date);
    date.setUTCHours(0, 0, 0, 0);
    return {
      ...item,
      date,
    };
  });
};

export const convertApiDataToPaymentMethods = (
  apiData: any[]
): PaymentMethod[] => {
  return apiData.map((item: any) => {
    return {
      id: item.id,
      type: item.type,
      name: item.name,
      last4: item.last4,
      exp_month: item.exp_month,
      exp_year: item.exp_year,
      default: item.default,
      brand: item.brand,
      payer_email: item.payer_email,
    };
  });
};

export const convertApiDataToPaymentMethod = (apiData: any): PaymentMethod => {
  return {
    id: apiData.id,
    name: apiData.billing_details.name,
    type: apiData.card.brand,
    last4: apiData.card.last4,
    exp_month: apiData.card.exp_month,
    exp_year: apiData.card.exp_year,
    default: false,
    brand: apiData.card.brand,
    payer_email: apiData.card.payer_email,
  };
};

const generateDateFromApiDataString = (
  stringDate: string | null
): Date | null => {
  if (!stringDate) {
    return null;
  }
  const date = new Date(stringDate);
  date.setUTCHours(0, 0, 0, 0);
  return date;
};

const convertFamilyOption = ({
  apiFamilyOption,
  externalAdminEmail,
}: {
  apiFamilyOption: any;
  externalAdminEmail: string | null;
}): FamilyOption | null => {
  if (
    !apiFamilyOption ||
    !isStringDatePresentOrFuture(apiFamilyOption.end_date)
  ) {
    return null;
  }

  return {
    id: apiFamilyOption.id,
    endDate: generateDateFromApiDataString(apiFamilyOption.end_date),
    isAutoRenewed: !apiFamilyOption.cancel_date,
    members: [],
    externalAdminEmail,
  };
};

const isStringDatePresentOrFuture = (stringDate: string) => {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);

  const date = generateDateFromApiDataString(stringDate);

  return !(today > date);
};
